import { useFeatureGate } from '@/features/billing/gates';
import { useEffectiveExperience } from '@/features/experiences/contexts/PageExperienceContext';

/**
 * The view-count gate. A free planner may hold a couple of views; adding beyond
 * that needs Plus.
 *
 * Scoped to the signed-in user rather than the experience: a view is a change the
 * person making it is entitled to, so a Plus collaborator can add one to a free
 * owner's planner.
 *
 * Shared by all three ways in — the sidebar's add button, the view picker's "add a
 * view" item, and the confirm step of the flow itself — so the allowance can't
 * drift between them. Existing views stay fully visible either way; only adding
 * another is gated.
 */
export const useAddViewGate = () => {
  // Views hang off the parent planner. Read through a printable child's context and
  // `children` is empty, which would report every planner as having no views and
  // leave the gate permanently open.
  const experience = useEffectiveExperience();

  const { isLocked, requireAccess, limit } = useFeatureGate('addView', {
    experience,
    count: experience.children.length,
  });

  // `isLocked` rather than `isGated` for the affordance: below the allowance it's
  // false regardless of tier, so this doesn't flash a crown while the plan loads —
  // but at the allowance it stays shut until entitlement is known.
  return { isAddViewGated: isLocked, runGatedAddView: requireAccess, limit };
};
